import {Database} from '@tnc/shared';

import {mediaGalleryImageT, mediaT} from '@/lib/types/media.types';
import {FlattenT} from '@/lib/types/utility.types';

import {defaultCreateData} from '../helpers';
import {SupabaseInstance} from './supabaseClass';

type TableNames = keyof {
  [K in keyof Database['public']['Tables'] as K extends `${string}_Media` ? K : never]: Database['public']['Tables'][K];
};
type TableKeys<T extends TableNames> = keyof Database['public']['Tables'][T]['Insert'];
type MediaObjT<T extends TableNames> = FlattenT<T & {Media: mediaT}>;
type ExcludeMediaId<T extends TableNames> = Exclude<TableKeys<T>, 'media_id'>;

class MediaClass {
  /**
   * Gets the media for a specific table and id.
   * @param {string} table - The table name.
   * @param {number} id - The id of the record.
   * @param {string} key - The key to match the id.
   * @returns {Promise<any>} - The media records.
   */
  public async getImages<T extends TableNames, K extends TableKeys<T>>(table: T, id: number, key: K) {
    try {
      // Fetch images based on the table, id, and key
      const {data, error} = await SupabaseInstance.supabase
        .from(table)
        .select(`*, Media(*)`)
        .eq(key as string, id)
        .returns<MediaObjT<T>[]>();

      if (error) {
        console.error('Error getting images', error);
        return [];
      }

      return await this.createSignedUrls(data);
    } catch (error) {
      console.error('Error getting images', error);
    }
  }

  /**
   * Uploads media to storage and the database
   * @param {string} table - The table name.
   * @param {number} id - The id of the record.
   * @param {string} key - The key to match the id.
   * @param {FormData} formData - The form data to upload.
   * @returns {Promise<any>} - The media records.
   */
  public async uploadImages<T extends TableNames, K extends ExcludeMediaId<T>>({
    bucket,
    path,
    table,
    id,
    key,
    formData,
  }: {
    bucket: string;
    path: string;
    table: T;
    id: number;
    key: K; // Explicitly typed to exclude 'media_id' with proper inference
    formData: FormData;
  }) {
    try {
      const mediaRecords: Database['public']['Tables'][T]['Insert'][] = [];
      const createData = await defaultCreateData();

      for (const file of formData.getAll('file')) {
        const timestamp = new Date().getTime();
        const uniqueKey = `${id}-${timestamp}`;
        const newFile = file as File;

        const {data: media, error: mediaError} = await SupabaseInstance.supabase.storage
          .from(bucket)
          .upload(`${path}/${uniqueKey}`, file);

        if (mediaError) {
          console.error('Error uploading media', mediaError);
          continue; // Skip this file and proceed with the next one
        }

        const media_file_name = newFile.name;
        const media_file_extension = newFile.type;

        const {data: insertedMedia, error: insertMediaError} = await SupabaseInstance.supabase
          .from('Media')
          .insert({
            media_bucket: bucket,
            media_file_extension,
            media_file_name,
            media_full_path: media.fullPath,
            media_path: media.path,
            media_alt: `${media_file_name}`,
            ...createData,
          })
          .select('media_id')
          .single();

        if (insertMediaError) {
          console.error('Error inserting media', insertMediaError);
          continue; // Skip this file and proceed with the next one
        }

        if (media) {
          const record: Database['public']['Tables'][T]['Insert'] = {
            media_id: insertedMedia?.media_id,
            [key]: id,
          } as unknown as Database['public']['Tables'][T]['Insert'];

          mediaRecords.push(record);
        }
      }

      if (mediaRecords.length > 0) {
        const {data, error} = await SupabaseInstance.supabase
          .from(table)
          .insert(mediaRecords)
          .select('*, Media(*)')
          .returns<MediaObjT<T>[]>();

        if (error) {
          throw new Error('Error inserting media records');
        }

        return await this.createSignedUrls(data);
      } else {
        throw new Error('No media records to insert');
      }
    } catch (error) {
      console.error('Error uploading images', error);
      throw new Error('Error uploading images');
    }
  }

  /**
   * Takes in records with Media and returns the signed URLs in the mediaGalleryImageT format
   * @param {MediaObjT[]} records - The records with Media.
   * @returns {Promise<mediaGalleryImageT[]>} - The media records.
   */
  private async createSignedUrls<T extends TableNames>(records: MediaObjT<T>[]): Promise<mediaGalleryImageT[]> {
    return await Promise.all(
      records.map(async image => {
        const {data: signedUrlData, error: signedUrlError} = await SupabaseInstance.supabase.storage
          .from('media')
          .createSignedUrl(image.Media.media_path, 2000);

        if (!signedUrlData || signedUrlError) {
          throw new Error('Error getting signed URL');
        }

        return {
          url: signedUrlData.signedUrl,
          id: image.Media.media_id,
          alt: image.Media.media_alt || 'gallery image',
          date_created: image.Media.date_created,
          date_removed: image.Media.date_removed,
        };
      })
    );
  }
}

const MediaInstance = new MediaClass();

export {MediaInstance};
